// Assay Compositing Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';

class AssayCompositingService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoints for assay results
    this.assayResultsUrl = `${this.functionAppUrl}/api/assay_results`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();
      
      // Get user's group memberships for authorization
      const userGroups = await authService.getUserGroups();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
        'x-user-groups': JSON.stringify(userGroups) // Pass group IDs as JSON array
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get assay results from database
   * Returns raw assay data for compositing
   */
  async getAssayResults(options = {}) {
    try {
      const {
        limit = 1000,
        offset = 0,
        holeId = null
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.assayResultsUrl}?limit=${limit}&offset=${offset}`;
      
      if (holeId) {
        url += `&hole_id=${encodeURIComponent(holeId)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch assay results: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: result.success,
        data: result.data || [],
        message: result.message
      };
    } catch (error) {
      console.error('Error fetching assay results:', error);
      throw error;
    }
  }

  /**
   * Get only hole IDs for populating dropdown (lightweight)
   */
  async getHoleIds() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const url = `${this.assayResultsUrl}?holes_only=true`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch hole IDs: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: result.success,
        holeIds: result.data || [],
        message: result.message
      };
    } catch (error) {
      console.error('Error fetching hole IDs:', error);
      throw error;
    }
  }

  /**
   * Get assay data for a specific hole (lazy loading)
   */
  async getHoleData(holeId) {
    try {
      const result = await this.getAssayResults({ 
        holeId, 
        limit: 10000 
      });
      
      // Transform backend data to match our expected format
      const transformedData = (result.data || []).map(row => ({
        hole_id: row.hole_id || row.holeid || '',
        sample_number: row.sample_number || row.sample_id || '',
        from_depth: parseFloat(row.from_depth || row.from || 0),
        to_depth: parseFloat(row.to_depth || row.to || 0),
        cu_best: parseFloat(row.cu_best || row.cu || 0),
        au_best: parseFloat(row.au_best || row.au || 0),
        ag_best: parseFloat(row.ag_best || row.ag || 0),
        rock_type: row.rock_type || row.lithology || row.lith || ''
      }));
      
      return transformedData;
    } catch (error) {
      console.error(`Error fetching data for hole ${holeId}:`, error);
      throw error;
    }
  }

  /**
   * Get block model data for a specific hole (lazy loading)
   */
  async getBlockModelData(holeId) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const url = `${this.functionAppUrl}/api/block_model_data?hole_id=${encodeURIComponent(holeId)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch block model data: ${response.statusText}`);
      }

      const result = await response.json();
      
      const parseGrade = (v) => (v == null || v === '' ? null : parseFloat(v));

      // Transform to match our expected format
      const transformedData = (result.data || []).map(row => {
        const matched =
          row.match_status === 'MATCHED' ||
          (row.id != null && row.match_status !== 'NO BLOCK MATCH');
        return {
          sample_number: row.sample_number,
          has_block_match: matched,
          match_status: row.match_status,
          cu_block: parseGrade(row.cu_block),
          au_block: parseGrade(row.au_block),
          ag_block: parseGrade(row.ag_block),
          recov_cu: parseGrade(row.recov_cu),
          recov_au: parseGrade(row.recov_au),
          recov_ag: parseGrade(row.recov_ag),
          zone: row.zone,
          class: row.class,
          cueq: parseGrade(row.cueq),
          intersecting_block_count: row.intersecting_block_count,
          center_distance_m: parseGrade(row.center_distance_m)
        };
      });
      
      return transformedData;
    } catch (error) {
      console.error(`Error fetching block model data for hole ${holeId}:`, error);
      throw error;
    }
  }

  /**
   * Get all assay data for the compositing interface (now returns only hole IDs)
   */
  async getDashboardData() {
    try {
      // Fetch only hole IDs for initial load (lightweight)
      const holeIdsResult = await this.getHoleIds();

      return {
        holeIds: holeIdsResult.holeIds || [],
        success: true,
        source: 'database'
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Parse CSV file for ad-hoc analysis
   */
  parseCSVFile(file) {
    return new Promise((resolve, reject) => {
      // Check if PapaParse is available (we'll need to add it)
      if (typeof window.Papa === 'undefined') {
        reject(new Error('CSV parser not loaded. Please ensure PapaParse is included.'));
        return;
      }

      window.Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results) => {
          try {
            const headers = results.meta.fields || [];
            const rawRows = results.data || [];
            resolve({ headers, rawRows });
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => {
          reject(new Error(`CSV parsing failed: ${error.message}`));
        }
      });
    });
  }

  /**
   * Apply header mapping to raw CSV data
   */
  applyHeaderMapping(rawRows, mapping) {
    const toNumber = (value) => {
      const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
      return Number.isFinite(n) ? n : NaN;
    };

    return rawRows
      .map((row) => {
        const mapped = {
          hole_id: String(row[mapping.hole_id] ?? '').trim(),
          sample_number: String(row[mapping.sample_number] ?? '').trim(),
          from_depth: toNumber(row[mapping.from_depth]),
          to_depth: toNumber(row[mapping.to_depth]),
          cu_best: toNumber(row[mapping.cu_best]),
          au_best: toNumber(row[mapping.au_best]),
          ag_best: toNumber(row[mapping.ag_best]),
          rock_type: String(row[mapping.rock_type] ?? '').trim()
        };

        // Validate required fields
        if (
          !mapped.hole_id ||
          !mapped.sample_number ||
          Number.isNaN(mapped.from_depth) ||
          Number.isNaN(mapped.to_depth) ||
          Number.isNaN(mapped.cu_best) ||
          Number.isNaN(mapped.au_best) ||
          Number.isNaN(mapped.ag_best)
        ) {
          return null;
        }

        return mapped;
      })
      .filter((r) => r !== null);
  }

  /**
   * Filter assay data by hole_id search term
   */
  filterByHoleId(data, searchTerm) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(row => 
      row.hole_id?.toLowerCase().includes(term)
    );
  }

  /**
   * Sort assay data by field and direction
   */
  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      const aVal = a[field] ?? '';
      const bVal = b[field] ?? '';
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }

  /**
   * Get unique holes from assay data
   */
  getUniqueHoles(data) {
    return Array.from(new Set(data.map(r => r.hole_id))).sort();
  }

  /**
   * Get depth range for a specific hole
   */
  getHoleDepthRange(data, holeId) {
    const holeData = data.filter(r => r.hole_id === holeId);
    if (!holeData.length) return null;
    
    const minFrom = Math.min(...holeData.map(r => r.from_depth));
    const maxTo = Math.max(...holeData.map(r => r.to_depth));
    
    return { minFrom, maxTo };
  }

  /**
   * Export data to CSV
   */
  exportToCSV(filename, data) {
    if (typeof window.Papa === 'undefined') {
      console.warn('PapaParse not available, using basic CSV export');
      this.basicCSVExport(filename, data);
      return;
    }

    const csv = window.Papa.unparse(data);
    this.downloadCSV(filename, csv);
  }

  /**
   * Basic CSV export fallback
   */
  basicCSVExport(filename, data) {
    if (!data || !data.length) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] ?? '';
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ].join('\n');
    
    this.downloadCSV(filename, csvContent);
  }

  /**
   * Download CSV file
   */
  downloadCSV(filename, csvContent) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const assayCompositingService = new AssayCompositingService();
export default assayCompositingService;
